import React, { useEffect, useMemo, useState, useCallback } from "react";

import { FieldArray } from "formik";

import {
  Input,
  Checkbox,
  Select,
  Button,
  GooglePlacesAutoComplete,
  renderAdditionalRideOptions,
} from "../..";

import { LodashHelper } from "../../../utils";
import { getCitiesForCounties, handleCitiesSelect } from "../../../utils/countiesHelper";
import { TagsList } from "./TagsList";
import {
  INVALID_ADDRESS_MESSAGE,
  OPERATIONAL_WORKING_HOUR_TYPE,
  USER_ROLES,
  VEHICLE_OPTIONS,
  VEHICLE_TYPES,
} from "../../../constants/constants";
import { DailyReminderNotification } from "./DailyReminderNotification";

import styles from "./index.module.css";
import FleetSize from "./FleetSize";
import TCOperationHours from "./TCOperationHours";
import { useSelector } from "react-redux";

const renderVehicleTypes =
  (renderedValues, values, name, isFieldDisabled) => (arrayHelpers) => {
    const handleCheckbox =
      (option) =>
        ({ checked }) => {
          if (checked) arrayHelpers.push(option.value);
          else {
            const idx = values[name].indexOf(option.value);
            arrayHelpers.remove(idx);
          }
        };
    return renderedValues.map((option) => (
      <label className={styles.checkbox} key={option.value}>
        <Checkbox
          name={name}
          checked={
            values && values[name] && values[name].includes(option.value)
          }
          value={option.value}
          onChange={handleCheckbox(option)}
          disabled={isFieldDisabled}
        />
        <span className={styles.checkboxLabel}>{option.title}</span>
      </label>
    ));
  };

export const CreateTC = ({
  personalInfoFieldsDisabled,
  values,
  switchAdmin,
  handleSelect,
  handleInviteUser,
  handleChangeAdmin,
  countyValue,
  handleStateSelect,
  handleCountiesSelect,
  handleAddCounties,
  handleDelete,
  showPersonalInfo = true,
  handleSelectAddress,
  handleChangeAddress,
  disableAllFields,
  hideUberVehicleOption = false,
  rideOptions,
  states,
  counties,
  cities,

  ...rest
}) => {

  const { validateForm } = rest;
  useEffect(() => { validateForm(); return; }, [values?.location?.formattedAddress, validateForm])

  const [countiesDropdownKey, setCountiesDropdownKey] = useState(0);

  const onStateSelect = useCallback(
    (args) => {
      handleStateSelect(args);
      setCountiesDropdownKey((key) => key + 1);
    },
    [handleStateSelect]
  );

  const onCountiesSelect = useCallback(
    (args) => {
      handleCountiesSelect(args);
    },
    [handleCountiesSelect]
  );

  const availableCities = useMemo(
    () =>
      values.state && values.counties?.length
        ? getCitiesForCounties(values.state, values.counties, counties[values.state], cities)
        : [],
    [cities, counties, values.counties, values.state]
  );

  const user = useSelector((state) => state.authorization.user);

  const handleDeleteTag =
    ({ values, setValues }) =>
      ({ state, county, city, deleteState, deleteAllCities }) =>
        handleDelete({ values, setValues, state, county, city, deleteState, deleteAllCities });

  const countiesOptions = useMemo(
    () =>
      values.state && !LodashHelper.isEmpty(counties)
        ? [
          {
            title: "Select All",
            value: "Select All",
          },
          ...counties[values.state].map((c) => ({ title: c, value: c })),
        ]
        : [],
    [counties, values.state]
  );

  const citiesOptions = useMemo(
    () =>
      availableCities.length
        ? [
          ...(availableCities.length > 1
            ? [{ title: "Select All", value: "Select All" }]
            : []),
          ...availableCities.map((city) => ({ title: city, value: city })),
        ]
        : [],
    [availableCities]
  );

  const savedOperational = useMemo(
    () => values.operationalCity?.find((item) => item.state === values.state),
    [values.operationalCity, values.state]
  );

  const handleCountiesBtnDisabled = useMemo(
    () =>
      (!values.counties?.length &&
        !Boolean(
          values.regionsOfOperation?.find(
            (region) => region.state === values.state
          )
        )) ||
      (availableCities.length > 0 && !values.cities?.length) ||
      (LodashHelper.isEqual(values.counties, savedOperational?.counties) &&
        LodashHelper.isEqual(values.cities, savedOperational?.cities || [])),
    [availableCities.length, savedOperational, values.cities, values.counties, values.regionsOfOperation, values.state]
  );

  const handleCountiesBtnText = useMemo(
    () => (savedOperational ? "Update Cities" : "Add Cities"),
    [savedOperational]
  );

  const vehicleOptions = useMemo(
    () =>
      hideUberVehicleOption
        ? VEHICLE_OPTIONS.filter(
          (option) => option.value !== VEHICLE_TYPES.UBER_AMBULATORY_RIDE
        )
        : VEHICLE_OPTIONS,
    [hideUberVehicleOption]
  );

  return (
    <>
      <label className={styles.organizationName}>
        <span className={styles.required}>Company Name</span>
        <Input
          name="name"
          type="text"
          placeholder="Company Name"
          disabled={disableAllFields}
        />
      </label>
      <label className={styles.organizationLocation}>
        <span className={styles.required}>Address</span>
        <GooglePlacesAutoComplete
          name="location.formattedAddress"
          value={values?.location?.formattedAddress}
          handleSelectAddress={handleSelectAddress}
          handleChangeAddress={handleChangeAddress}
          disabled={disableAllFields}
        />
        {rest.touched &&
          rest.touched["location.formattedAddress"] &&
          rest?.errors.location && (
            <div className={styles.errorMessage} role="alert">
              {INVALID_ADDRESS_MESSAGE}
            </div>
          )}
      </label>
      <div className={styles.regions}>
        <span className={styles.states}>Counties of Operation</span>

        <div className={styles.address}>
          {!disableAllFields && (
            <>
              <label className={styles.statesSelect}>
                State(s)
                <Select
                  showSearch
                  name="state"
                  options={states}
                  onChange={onStateSelect}
                  placeholder="State"
                  disabled={disableAllFields}
                />
              </label>

              <label className={styles.countiesSelect}>
                County
                <Select
                  key={`counties-select-${countiesDropdownKey}`}
                  name="counties"
                  options={countiesOptions}
                  onChange={onCountiesSelect}
                  placeholder="Pick a county"
                  mode="multiple"
                  maxTagCount={1}
                  maxTagTextLength={15}
                  disabled={!values.state}
                  defaultOpen={Boolean(values.state)}
                  getPopupContainer={(trigger) => document.body}
                  myClassName={styles.regionsMultiSelect}
                />
              </label>

              <label className={styles.citiesSelect}>
                City
                <Select
                  showSearch
                  name="cities"
                  options={citiesOptions}
                  onChange={handleCitiesSelect(availableCities)}
                  placeholder="Pick a city"
                  mode="multiple"
                  maxTagCount={1}
                  maxTagTextLength={15}
                  disabled={!values.counties?.length}
                  listHeight={256}
                  virtual
                  myDropdownClassName={styles.citiesDropdown}
                  getPopupContainer={(trigger) => document.body}
                  myClassName={styles.regionsMultiSelect}
                />
              </label>

              <div className={styles.addCounties}>
                <Button
                  onClick={handleAddCounties}
                  disabled={handleCountiesBtnDisabled}
                >
                  {handleCountiesBtnText}
                </Button>
              </div>
            </>
          )}
        </div>

        {values.regionsOfOperation.map((region) => {
          const operational = values.operationalCity?.find((item) => item.state === region.state);

          return (
            <TagsList
              info={{ ...region, cities: operational?.cities || region.cities || [] }}
              key={region.state}
              handleDeleteTag={handleDeleteTag({
                values,
                setValues: rest.setValues,
              })}
              isFieldDisabled={disableAllFields}
            />
          );
        })}
      </div>
      {showPersonalInfo && (
        <>
          <div className={styles.name}>
            <label className={styles.nameLabel}>
              <span className={styles.required}>
                Administrator's First Name
              </span>
              <Input
                name="admin.firstName"
                type="text"
                placeholder="Administrator's First Name"
                disabled={personalInfoFieldsDisabled}
              />
            </label>
            <label className={styles.nameLabel}>
              <span className={styles.required}>Administrator's Last Name</span>
              <Input
                name="admin.lastName"
                type="text"
                placeholder="Administrator's Last Name"
                disabled={personalInfoFieldsDisabled}
              />
            </label>
          </div>
          <div className={styles.emailContainer}>
            <label className={styles.email}>
              <span className={styles.required}> Admin's Email</span>
              <Input
                name="admin.email"
                type="text"
                placeholder="Admin's Email"
                disabled={personalInfoFieldsDisabled}
              />
            </label>
            {switchAdmin && (
              <div className={styles.changeAdminBtn}>
                <Button
                  myClassName={styles.formControlBtn}
                  onClick={handleChangeAdmin}
                >
                  Switch Admin
                </Button>
              </div>
            )}
          </div>

          {switchAdmin && (
            <div className={styles.inviteBtn}>
              <Button
                myClassName={styles.formControlBtn}
                onClick={handleInviteUser}
              >
                Invite New User
              </Button>
            </div>
          )}
        </>
      )}
      <div className={styles.checkboxesColumn}>
        <label className={styles.rideRequestFieldsHead}>Vehicle Type(s)</label>
        <div className={styles.checkboxesList}>
          <FieldArray
            name="vehicleTypes"
            render={renderVehicleTypes(
              vehicleOptions,
              values,
              "vehicleTypes",
              disableAllFields
            )}
          />
        </div>
      </div>
      
      {user?.role === USER_ROLES.SUPER_ADMIN && (
        <label className={styles.bufferField}>
          Driver Scheduling Buffer (minutes)
          <Input
            name="driverSchedulingBuffer"
            type="number"
            min={0}
            max={1440}
            placeholder="30"
            disabled={disableAllFields}
          />
        </label>
      )}

      <div className={styles.carSeatColumn} key={"cbS"}>
        {/* <label className={styles.rideRequestFieldsHead}>Car/Booster Seat </label>
        <label className={styles.checkbox}>
          <Checkbox name='supportCarBoosterSeat' checked={values?.supportCarBoosterSeat}  disabled={disableAllFields}/>
          <span className={styles.checkboxLabel}>Allow TC to View and Accept Car/Booster Seat Ride Requests</span>
        </label> */}
      </div>

      <div className={styles.checkboxesColumn} key={"vcD"}>
        <label className={styles.rideRequestFieldsHead}>Fleet Size(s)</label>
        <div className={styles.checkboxesRow}>

          <FleetSize values={values?.fleetSizes} name={"fleetSizes"} isFieldDisabled={disableAllFields} />
        </div>
      </div>
      {/* {TCOperationHours({
        values: values?.hourOfOperations?.weekdays,
        type: OPERATIONAL_WORKING_HOUR_TYPE.WEEKLY,
        fieldName: "hourOfOperations.weekdays",
        isFieldsDisabled: disableAllFields,
        title: "Hours Of Operation (normal business hours)",
      })} */}

      <TCOperationHours
        values={values?.hourOfOperations?.weekdays}
        type={OPERATIONAL_WORKING_HOUR_TYPE.WEEKLY}
        fieldName={"hourOfOperations.weekdays"}
        isFieldsDisabled={disableAllFields}
        title={"Hours Of Operation (normal business hours)"}
      />

      {/* {TCOperationHours({
        values: values?.hourOfOperations?.holiday,
        type: OPERATIONAL_WORKING_HOUR_TYPE.HOLIDAY,
        fieldName: "hourOfOperations.holiday",
        isFieldsDisabled: disableAllFields,
        title: "Holidays",
      })} */}

      <TCOperationHours
        values={values?.hourOfOperations?.holiday}
        type={OPERATIONAL_WORKING_HOUR_TYPE.HOLIDAY}
        fieldName={"hourOfOperations.holiday"}
        isFieldsDisabled={disableAllFields}
        title={"Holidays"}
      />
      {rideOptions?.length > 0 && (
        <div className={styles.additionalOptions}>
          <span className={styles.additionalOptionsHead}>
            Additional driver/vehicle capabilities
          </span>
          <div className={styles.checkboxesRow}>
            <FieldArray
              name="rideOptions"
              render={renderAdditionalRideOptions({
                renderedValues: rideOptions,
                values,
                name: "rideOptions",
                disabled: disableAllFields,
                showTooltip: false,
              })}
            />
          </div>
        </div>
      )}
      <DailyReminderNotification values={values} disabled={disableAllFields} />
    </>
  );
};
